import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const Cookies = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-8">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border-4 border-blue-600">
          <h1 className="text-4xl font-black text-gray-900 mb-6">Cookies Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: December 2025</p>
          
          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">1. What Are Cookies</h2>
              <p>
                Cookies are small text files that are placed on your device when you visit our website or use our 
                application. They help us provide you with a better experience by remembering your preferences, 
                understanding how you use our services, and improving our platform's functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Types of Cookies We Use</h2>
              
              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Essential Cookies</h3>
                  <p>
                    These cookies are necessary for the website to function properly. They enable core functionality 
                    such as security, network management, and accessibility. You cannot opt-out of these cookies.
                  </p>
                  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
                    <li>Authentication and session management</li>
                    <li>Security and fraud prevention</li>
                    <li>Load balancing and server optimization</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Analytics Cookies</h3>
                  <p>
                    These cookies help us understand how visitors interact with our website and application by 
                    collecting and reporting information anonymously.
                  </p>
                  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
                    <li>Page views and navigation patterns</li>
                    <li>Time spent on pages</li>
                    <li>Feature usage statistics</li>
                    <li>Performance monitoring</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Functionality Cookies</h3>
                  <p>
                    These cookies enable enhanced functionality and personalization, such as remembering your 
                    preferences and settings.
                  </p>
                  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
                    <li>Language preferences</li>
                    <li>Display settings</li>
                    <li>Location preferences</li>
                    <li>Saved searches and filters</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Marketing Cookies</h3>
                  <p>
                    These cookies are used to track visitors across websites and deliver more relevant advertising. 
                    They may be set by us or third-party advertising partners.
                  </p>
                  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
                    <li>Targeted advertising</li>
                    <li>Campaign effectiveness measurement</li>
                    <li>Social media integration</li>
                    <li>Remarketing activities</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Third-Party Cookies</h2>
              <p className="mb-2">
                We may use third-party services that set cookies on your device. These include:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Stripe:</strong> For secure payment processing</li>
                <li><strong>Google Analytics:</strong> For website analytics and usage tracking</li>
                <li><strong>Social Media Platforms:</strong> For social sharing features</li>
                <li><strong>Email Services:</strong> For communication tracking</li>
              </ul>
              <p className="mt-3">
                These third parties have their own privacy policies and cookie policies, and we encourage you 
                to review them.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">4. How Long Cookies Last</h2>
              <p className="mb-2">Cookies can be either:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Session Cookies:</strong> These are temporary and are deleted when you close your browser. 
                  They help us maintain your session as you navigate through our platform.
                </li>
                <li>
                  <strong>Persistent Cookies:</strong> These remain on your device for a set period or until you 
                  delete them. They help us remember your preferences and provide a more personalized experience.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Managing Your Cookie Preferences</h2>
              <p className="mb-3">
                You have the right to decide whether to accept or reject cookies. You can set or amend your 
                cookie preferences in the following ways:
              </p>
              
              <div className="space-y-3 ml-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Browser Settings</h3>
                  <p>
                    Most web browsers automatically accept cookies, but you can modify your browser settings 
                    to decline cookies if you prefer. Please note that disabling cookies may affect your ability 
                    to use certain features of our platform.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Cookie Preference Center</h3>
                  <p>
                    When you first visit our website, you'll see a cookie consent banner where you can choose 
                    which types of cookies to accept. You can update your preferences at any time by accessing 
                    our Cookie Preference Center in the footer of our website.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Mobile App Settings</h3>
                  <p>
                    For our mobile application, you can manage cookie-like tracking technologies through your 
                    device settings and app permissions.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Do Not Track Signals</h2>
              <p>
                Some browsers have a "Do Not Track" feature that lets you tell websites that you do not want 
                to have your online activities tracked. At this time, we do not respond to browser "Do Not Track" 
                signals, but we provide you with the ability to manage your cookie preferences as described above.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Updates to This Policy</h2>
              <p>
                We may update this Cookies Policy from time to time to reflect changes in our practices or for 
                other operational, legal, or regulatory reasons. We will notify you of any material changes by 
                posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Contact Us</h2>
              <p className="mb-2">
                If you have any questions about our use of cookies or this Cookies Policy, please contact us at:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg ml-4">
                <p className="font-semibold">Email: support@fltrlnk.com</p>
                <p className="mt-2">
                  For more information about how we handle your personal data, please refer to our{' '}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </section>

            <section className="border-t-2 border-gray-200 pt-6 mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Cookie Reference Table</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left">Cookie Name</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">session_id</td>
                      <td className="border border-gray-300 px-4 py-2">Essential</td>
                      <td className="border border-gray-300 px-4 py-2">User authentication</td>
                      <td className="border border-gray-300 px-4 py-2">Session</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">user_preferences</td>
                      <td className="border border-gray-300 px-4 py-2">Functionality</td>
                      <td className="border border-gray-300 px-4 py-2">Store user settings</td>
                      <td className="border border-gray-300 px-4 py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">_ga</td>
                      <td className="border border-gray-300 px-4 py-2">Analytics</td>
                      <td className="border border-gray-300 px-4 py-2">Google Analytics tracking</td>
                      <td className="border border-gray-300 px-4 py-2">2 years</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">marketing_consent</td>
                      <td className="border border-gray-300 px-4 py-2">Marketing</td>
                      <td className="border border-gray-300 px-4 py-2">Track marketing preferences</td>
                      <td className="border border-gray-300 px-4 py-2">6 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cookies;
